import { Injectable } from '@angular/core';

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Payee } from '../models/payee';
import { Payee_entry, Payee_entry_move_result } from '../models/payee_entry';
import { Signature } from '../models/signature';

import { AppSettings } from '../app.settings';
import _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class PayeeService {

  constructor(private http: HttpClient) { }

  get(id: string): Promise<Payee> {
    return this.getWithRest("id/" + id);
  }

  getForEntry(entry_id: string): Promise<Payee> {
    return this.getWithRest("entry_id/" + entry_id);
  }

  private getWithRest(rest_url: string) : Promise<Payee> {
    return new Promise((resolve, reject) => {
    return this.http.get(AppSettings.API_ENDPOINT + 'payees/' + rest_url).subscribe((response:any) => {
      if(response.type == 'payee' && response.data){
        resolve(response.data);
      } else {
        reject(response);
      }

    },
      error_response => {
            if(error_response instanceof HttpErrorResponse){
              reject(error_response.error);
            }
            reject(error_response);
      });
  });
  }


	getList(): Promise<Payee[]> {
		return this.getListWithRest("");
	}

	private getListWithRest(rest_url: string) : Promise<Payee[]> {
  	return new Promise((resolve, reject) => {
		return this.http.get(AppSettings.API_ENDPOINT + 'payees/' + rest_url).subscribe((response:any) => {
			if(response.type == 'payee_list' && response.data){
				resolve(response.data);
			} else {
				reject(response);
			}

		},
			error_response => {
	      		if(error_response instanceof HttpErrorResponse){
	      			reject(error_response.error);
	      		}
	      		reject(error_response);
			});
	});
  }

  create(payee: Payee): Promise<Payee> {

    let post_data = {
        data: {
          type: 'payee',
          attributes: { payee: payee }
          }
      };

    	return new Promise((resolve, reject) => {

            return this.http.post(AppSettings.API_ENDPOINT + 'payees',
                post_data, AppSettings.DEFAULT_HTTP_OPTIONS).subscribe((response: any) => {
                     if(response.data && response.type=='payee') {
                       	 resolve(response.data);
                       }  else {
                         reject(response);
                       }

                }, error => {
                  if(error instanceof HttpErrorResponse){
                    reject(error.error);
                  } else {
                    reject(error);
                  }
                });
            });
    }

    /* A payee record is shared by every entry that points at it, including entries at shows
    * whose prize money has not been disbursed yet, so it is never edited in place.
    * Unchanged: reuse it. Changed: PUT, which the API saves as a NEW payee (the original plus these
    * changes) and records in payee_history, leaving the original untouched. New: POST.
    * original is the payee as loaded, before the user edited the form.
    */
    saveForEntry(payee: Payee, original: Payee): Promise<Payee> {
      if(payee.id && original && original.id == payee.id){
        if(_.isEqual(original, payee)){
          return Promise.resolve(payee);
        }
        return this.copyWithChanges(payee);
      }

      const new_payee = _.cloneDeep(payee);
      delete new_payee.id;
      return this.create(new_payee);
    }

    /* The user's other entries that use this W9 (excluding one entry), for carrying a correction to them. */
    getOtherEntriesUsingPayee(payee_id: string, exclude_entry_id: string): Promise<Payee_entry[]> {
      return new Promise((resolve, reject) => {
        this.http.get(AppSettings.API_ENDPOINT + 'payee_entries/payee_id/' + payee_id
          + (exclude_entry_id ? '/exclude_entry_id/' + exclude_entry_id : '')).subscribe((response: any) => {
            if(response.type == 'payee_entries') {
              resolve(response.data ?? []);
            } else {
              reject(response);
            }
          }, error => {
            reject(error instanceof HttpErrorResponse ? error.error : error);
          });
      });
    }

    /* Attach the corrected W9 to entries still using the one it replaces, recording the signer's one signature on
    * each. Every entry is checked again by the API; each comes back moved, or not with the reason.
    */
    moveEntriesToPayee(from_payee_id: string, payee_id: string, entry_ids: string[], signature: Signature): Promise<Payee_entry_move_result[]> {
      const post_data = {
        data: {
          type: 'payee_entry_moves',
          attributes: { from_payee_id: from_payee_id, payee_id: payee_id, entry_ids: entry_ids, signature: signature }
        }
      };
      return new Promise((resolve, reject) => {
        this.http.post(AppSettings.API_ENDPOINT + 'payee_entries', post_data, AppSettings.DEFAULT_HTTP_OPTIONS).subscribe((response: any) => {
            if(response.type == 'payee_entry_move_results') {
              resolve(response.data ?? []);
            } else {
              reject(response);
            }
          }, error => {
            reject(error instanceof HttpErrorResponse ? error.error : error);
          });
      });
    }

    private copyWithChanges(payee: Payee): Promise<Payee> {

      let put_data = {
        data: {
          type: 'payee',
          attributes: { payee: payee }
        }
      };

      return new Promise((resolve, reject) => {
        this.http.put(AppSettings.API_ENDPOINT + 'payees',
          put_data, AppSettings.DEFAULT_HTTP_OPTIONS).subscribe((response: any) => {
            if(response.data && response.type=='payee') {
              resolve(response.data);
            } else {
              reject(response);
            }
          }, error => {
            if(error instanceof HttpErrorResponse){
              reject(error.error);
            } else {
              reject(error);
            }
          });
      });
    }

}
